import { Injectable, Logger, NotFoundException } from '@nestjs/common';
import { createHash } from 'node:crypto';
import { PrismaService, S3CompatibleAdapter, StorageAdapter } from '@cart/api-data-access';
import { readPlatformStorageEnv } from '../media/platform-storage.constants';
import { buildInvoiceKey } from '../media/media-key.util';
import { financialYearLabel } from './financial-year';
import { InvoiceRendererService } from './invoice-renderer.service';
import { InvoiceLine, renderInvoiceHtml } from './invoice-template';
import { PartySnapshot, SellerSnapshot } from './invoice-snapshot';

export interface InvoicePdf {
  buffer: Buffer;
  fileName: string;
  invoiceNumber: string;
}

/**
 * The PDF half of invoicing: render once, store, then serve the stored bytes
 * for ever.
 *
 * Re-rendering on every download would be wrong twice over — it burns Chromium
 * time on work already done, and it means the document a customer downloads in
 * 2029 is whatever today's template produces rather than what was issued. The
 * stored object is the invoice; this class only fills it in the first time.
 */
@Injectable()
export class InvoicePdfService {
  private readonly logger = new Logger(InvoicePdfService.name);
  private storage: StorageAdapter | null = null;

  constructor(
    private readonly prisma: PrismaService,
    private readonly renderer: InvoiceRendererService
  ) {}

  /**
   * The PDF bytes for an already-issued invoice.
   *
   * Order of preference: the stored object, then a fresh render. A render that
   * cannot be stored is still returned to the caller — the customer gets their
   * invoice, and the next request retries the upload.
   */
  async pdfFor(invoiceId: string): Promise<InvoicePdf> {
    const invoice = await this.prisma.invoice.findUnique({ where: { id: invoiceId } });
    if (!invoice) throw new NotFoundException('Invoice not found');

    const fileName = `${invoice.invoiceNumber.replace(/[^A-Za-z0-9._-]+/g, '_')}.pdf`;

    if (invoice.pdfStorageKey) {
      const stored = await this.readStored(invoice.pdfStorageKey);
      if (stored) {
        // A stored object whose bytes no longer match what was recorded is a
        // real problem — corruption, or an overwrite that should be impossible.
        // Serve it (it is the issued document) but make the noise loud.
        if (invoice.pdfSha256 && sha256(stored) !== invoice.pdfSha256) {
          this.logger.error(
            `invoice ${invoice.invoiceNumber}: stored PDF does not match its recorded hash`
          );
        }
        return { buffer: stored, fileName, invoiceNumber: invoice.invoiceNumber };
      }

      this.logger.warn(
        `invoice ${invoice.invoiceNumber}: stored object ${invoice.pdfStorageKey} is missing — re-rendering`
      );
    }

    const buffer = await this.render(invoiceId);
    await this.store(invoiceId, buffer);

    return { buffer, fileName, invoiceNumber: invoice.invoiceNumber };
  }

  /** Builds the document model from frozen data and prints it. */
  private async render(invoiceId: string): Promise<Buffer> {
    const invoice = await this.prisma.invoice.findUniqueOrThrow({ where: { id: invoiceId } });

    const items = await this.prisma.orderItem.findMany({
      where: { orderId: invoice.orderId },
      orderBy: { sortOrder: 'asc' },
    });

    const lines: InvoiceLine[] = items.map((item) => {
      const gstAmount = item.igstAmount + item.cgstAmount + item.sgstAmount;
      const gstRateBps = item.isInterState
        ? item.igstRateBps
        : item.cgstRateBps + item.sgstRateBps;

      return {
        title: item.title,
        hsnCode: item.hsnCode,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        taxableValue: item.taxableValue || item.lineTotal - gstAmount,
        gstRateBps,
        gstAmount,
        lineTotal: item.lineTotal,
      };
    });

    const html = renderInvoiceHtml({
      invoiceNumber: invoice.invoiceNumber,
      issuedAt: invoice.issuedAt,
      kind: invoice.kind as 'tax_invoice' | 'credit_note',
      seller: invoice.sellerSnapshot as unknown as SellerSnapshot,
      buyer: invoice.buyerSnapshot as unknown as PartySnapshot,
      placeOfSupply: invoice.placeOfSupply,
      isInterState: invoice.isInterState,
      lines,
      taxableAmount: invoice.taxableAmount,
      cgstTotal: invoice.cgstTotal,
      sgstTotal: invoice.sgstTotal,
      igstTotal: invoice.igstTotal,
      tcsTotal: invoice.tcsTotal,
      shippingTotal: invoice.shippingTotal,
      discountTotal: invoice.discountTotal,
      grandTotal: invoice.grandTotal,
    });

    return this.renderer.render(html);
  }

  /**
   * Writes the PDF to R2 and records where it went.
   *
   * Failures are logged, not thrown: the caller already has the bytes, and a
   * storage outage should not deny a customer the invoice they asked for.
   */
  private async store(invoiceId: string, buffer: Buffer): Promise<void> {
    const invoice = await this.prisma.invoice.findUniqueOrThrow({ where: { id: invoiceId } });
    const storage = this.adapter();

    if (!storage || !invoice.organizationId) {
      this.logger.warn(
        `invoice ${invoice.invoiceNumber}: storage not configured — PDF served but not kept`
      );
      return;
    }

    const key = buildInvoiceKey({
      organizationId: invoice.organizationId,
      storeId: invoice.storeId,
      financialYear: financialYearLabel(invoice.financialYear),
      invoiceNumber: invoice.invoiceNumber,
      issuedAt: invoice.issuedAt,
    });

    try {
      await storage.save(buffer, key, 'application/pdf');

      // Only the PDF columns are writable — the immutability trigger refuses
      // any change to the number, snapshots or totals.
      await this.prisma.invoice.update({
        where: { id: invoiceId },
        data: {
          pdfStorageKey: key,
          pdfBytes: buffer.byteLength,
          pdfSha256: sha256(buffer),
          pdfGeneratedAt: new Date(),
        },
      });

      this.logger.log(`invoice ${invoice.invoiceNumber} stored at ${key}`);
    } catch (error) {
      this.logger.error(
        `invoice ${invoice.invoiceNumber}: could not store PDF — ${(error as Error).message}`
      );
    }
  }

  private async readStored(key: string): Promise<Buffer | null> {
    const storage = this.adapter();
    if (!storage) return null;

    try {
      return await storage.read(key);
    } catch {
      return null;
    }
  }

  /** The platform bucket. Same credentials and adapter as merchant media. */
  private adapter(): StorageAdapter | null {
    if (this.storage) return this.storage;

    const env = readPlatformStorageEnv();
    if (!env) return null;

    this.storage = new S3CompatibleAdapter({
      bucket: env.bucket,
      region: env.region,
      endpoint: env.endpoint,
      accessKeyId: env.accessKeyId,
      secretAccessKey: env.secretAccessKey,
      publicBaseUrl: env.publicBaseUrl,
      forcePathStyle: true,
    });

    return this.storage;
  }
}

function sha256(buffer: Buffer): string {
  return createHash('sha256').update(buffer).digest('hex');
}
